import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, HStack, RadioCard, Stack } from '@chakra-ui/react';
import { FieldError } from '@client/components/input/FieldError';
import { FormField, FormInput, } from '@client/components/input/FormField';
import { PriceInput } from '@client/components/input/PriceInput';
import { DayRangeInput, validateDayRange, } from '@client/components/listingForm/DayRangeInput';
import { AppDialog } from '@client/components/misc/AppDialog';
import { DialogConfirmFooter } from '@client/components/misc/DialogConfirmFooter';
import { InputSize } from '@client/constants';
import { validateShippingProfileInput } from '@heirloom/common/validation/profiles';
import { ValidationField, } from '@heirloom/common/validation/shared';
import { useEffect, useState } from 'react';
export var ShippingCostType;
(function (ShippingCostType) {
    ShippingCostType["Free"] = "free";
    ShippingCostType["FlatRate"] = "flat_rate";
})(ShippingCostType || (ShippingCostType = {}));
export const ShippingProfileDialog = ({ open, onClose, onConfirm, existingNames = [], saving = false, }) => {
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);
    const [originZip, setOriginZip] = useState('');
    const [zipError, setZipError] = useState(null);
    const [costType, setCostType] = useState(ShippingCostType.Free);
    const [flatRateCents, setFlatRateCents] = useState(null);
    const [flatRateError, setFlatRateError] = useState(null);
    const [minDays, setMinDays] = useState('');
    const [maxDays, setMaxDays] = useState('');
    const [daysError, setDaysError] = useState(null);
    useEffect(() => {
        if (!open) {
            setName('');
            setNameError(null);
            setOriginZip('');
            setZipError(null);
            setCostType(ShippingCostType.Free);
            setFlatRateCents(null);
            setFlatRateError(null);
            setMinDays('');
            setMaxDays('');
            setDaysError(null);
        }
    }, [open]);
    const isDirty = () => !!name ||
        !!originZip ||
        costType !== ShippingCostType.Free ||
        flatRateCents !== null ||
        !!minDays ||
        !!maxDays;
    const handleClose = () => {
        if (isDirty()) {
            if (!window.confirm('Discard changes?'))
                return;
        }
        onClose();
    };
    const handleConfirm = () => {
        const errors = validateShippingProfileInput({
            name,
            existingNames,
            originZip,
            isFlatRate: costType === ShippingCostType.FlatRate,
            flatShippingRateCents: flatRateCents,
            shippingDaysMin: minDays,
            shippingDaysMax: maxDays,
        });
        const findError = (field) => errors.find((e) => e.field === field)?.message ?? null;
        setNameError(findError(ValidationField.Name));
        setZipError(findError(ValidationField.OriginZip));
        setFlatRateError(findError(ValidationField.FlatRate));
        setDaysError(findError(ValidationField.Days));
        if (errors.length > 0)
            return;
        const days = validateDayRange(minDays, maxDays);
        const cost = costType === ShippingCostType.Free
            ? { type: ShippingCostType.Free }
            : {
                type: ShippingCostType.FlatRate,
                cents: flatRateCents ?? 0,
            };
        void onConfirm({
            name: name.trim(),
            originZip: originZip.trim(),
            cost,
            minDays: days.min,
            maxDays: days.max,
        });
    };
    return (_jsx(AppDialog, { title: "New Shipping Profile", open: open, onCancel: handleClose, pending: saving, size: "sm", footer: _jsx(DialogConfirmFooter, { onCancel: handleClose, onConfirm: handleConfirm, confirmLabel: "Create", pending: saving }), children: _jsxs(Stack, { gap: 4, children: [_jsx(FormField, { label: "Name", error: nameError, required: true, children: _jsx(FormInput, { value: name, onChange: (e) => {
                            setName(e.target.value);
                            if (e.target.value.trim())
                                setNameError(null);
                        }, placeholder: "e.g. Standard Domestic", disabled: saving }) }), _jsx(FormField, { label: "Origin zip code", error: zipError, required: true, children: _jsx(FormInput, { w: 32, value: originZip, onChange: (e) => {
                            const digits = e.target.value
                                .replace(/\D/g, '')
                                .slice(0, 5);
                            setOriginZip(digits);
                            if (digits)
                                setZipError(null);
                        }, placeholder: "e.g. 90210", inputMode: "numeric", disabled: saving }) }), _jsx(FormField, { label: "Cost", children: _jsx(RadioCard.Root, { value: costType, onValueChange: (e) => setCostType(e.value), size: "sm", alignSelf: "stretch", disabled: saving, children: _jsxs(HStack, { gap: 3, align: "start", children: [_jsxs(RadioCard.Item, { value: ShippingCostType.Free, children: [_jsx(RadioCard.ItemHiddenInput, {}), _jsxs(RadioCard.ItemControl, { alignItems: "center", children: [_jsx(RadioCard.ItemText, { fontSize: 16, children: "Free" }), _jsx(RadioCard.ItemIndicator, {})] })] }), _jsxs(RadioCard.Item, { value: ShippingCostType.FlatRate, children: [_jsx(RadioCard.ItemHiddenInput, {}), _jsx(RadioCard.ItemControl, { children: _jsxs(Stack, { width: "100%", gap: 3, children: [_jsxs(HStack, { alignItems: "center", justifyContent: "space-between", children: [_jsx(RadioCard.ItemText, { fontSize: 16, children: "Flat rate" }), _jsx(RadioCard.ItemIndicator, {})] }), costType ===
                                                        ShippingCostType.FlatRate && (_jsxs(Box, { onPointerDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: [_jsx(PriceInput, { size: InputSize.Md, value: flatRateCents, onChange: (v) => {
                                                                    setFlatRateCents(v);
                                                                    if (v &&
                                                                        v > 0)
                                                                        setFlatRateError(null);
                                                                }, invalid: !!flatRateError, enclosed: true }), flatRateError && (_jsx(FieldError, { children: flatRateError }))] }))] }) })] })] }) }) }), _jsx(FormField, { label: "Delivery", error: daysError, required: true, children: _jsx(DayRangeInput, { minDays: minDays, maxDays: maxDays, onChangeMin: (v) => {
                            setMinDays(v);
                            setDaysError(null);
                        }, onChangeMax: (v) => {
                            setMaxDays(v);
                            setDaysError(null);
                        }, disabled: saving }) })] }) }));
};
